/**
 * Environment configuration utilities
 * Handles Vite environment variables with proper validation and typing
 */

interface AppConfig {
  supabase: {
    url: string;
    anonKey: string;
    serviceRoleKey?: string;
  };
  app: {
    title: string;
    description: string;
  };
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Validates and returns environment variables
 * Throws an error if required variables are missing
 */
const getEnvVar = (key: keyof ImportMetaEnv, defaultValue?: string): string => {
  const value = import.meta.env[key] || defaultValue;
  
  if (!value) {
    throw new Error(
      `Missing required environment variable: ${key}. ` +
      `Please check your .env file and ensure it's properly configured.`
    );
  }
  
  return value;
};

/**
 * Application configuration object
 * All environment variables are validated and typed here
 */
export const config: AppConfig = {
  supabase: {
    url: getEnvVar('VITE_SUPABASE_URL'),
    anonKey: getEnvVar('VITE_SUPABASE_ANON_KEY'),
    serviceRoleKey: import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY,
  },
  app: {
    title: getEnvVar('VITE_APP_TITLE', 'Refine + Supabase App'),
    description: getEnvVar('VITE_APP_DESCRIPTION', 'A modern admin panel built with Refine and Supabase'),
  },
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
};

/**
 * Validates that all required environment variables are present
 * Call this early in your app initialization
 */
export const validateEnvironment = (): void => {
  try {
    // This will throw if any required variables are missing
    config.supabase.url;
    config.supabase.anonKey;
    
    if (config.isDevelopment) {
      console.log('✅ Environment variables validated successfully');
      console.log('🔧 Running in development mode');
    }
  } catch (error) {
    console.error('❌ Environment validation failed:', error);
    throw error;
  }
};

/**
 * Helper to check if we're in development mode
 */
export const isDev = (): boolean => config.isDevelopment;

/**
 * Helper to check if we're in production mode  
 */
export const isProd = (): boolean => config.isProduction;
